/**
 * ChangesImport.js — Änderungsverlauf Import
 * v1.1 | 2026-03-28 — AZG-Gruppierung, Nutzer-Anzeige
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdsDecisions from './AdsDecisions';
import './imports-shared.css';
import './ChangesImport.css';

var API = '/api/ads_changes_import.php';

var CHANGE_TYPES = [
    { key: 'pausiert',      label: 'Pausiert',       cls: 'chg-pause' },
    { key: 'aktiviert',     label: 'Aktiviert',      cls: 'chg-active' },
    { key: 'hinzugefügt',   label: 'Hinzugefügt',    cls: 'chg-add' },
    { key: 'entfernt',      label: 'Entfernt',       cls: 'chg-remove' },
    { key: 'geändert',      label: 'Geändert',       cls: 'chg-edit' },
];

function getChangeCls(text) {
    var t = (text || '').toLowerCase();
    if (t.indexOf('pausiert') >= 0) return 'chg-pause';
    if (t.indexOf('aktiviert') >= 0) return 'chg-active';
    if (t.indexOf('entfernt') >= 0) return 'chg-remove';
    if (t.indexOf('hinzugefügt') >= 0) return 'chg-add';
    if (t.indexOf('geändert') >= 0) return 'chg-edit';
    return '';
}

export default function ChangesImport({ campaignId, campaignName, selectedIds, campaignList, von1, bis1 }) {
    var [data, setData]           = useState([]);
    var [collapsedPeriods, setCollapsedPeriods] = useState({});
    var [loading, setLoading]     = useState(false);
    var [showForm, setShowForm]   = useState(false);
    var [importing, setImporting] = useState(false);
    var [preview, setPreview]     = useState(null);
    var [importMsg, setImportMsg] = useState(null);
    var [datumVon, setDatumVon]   = useState(von1 || '');
    var [datumBis, setDatumBis]   = useState(bis1 || '');
    var [rawText, setRawText]     = useState('');
    var [showKampagne, setShowKampagne] = useState(true);
    var [showAZG, setShowAZG]           = useState(true);

    var token = localStorage.getItem('token') || '';

    var load = useCallback(function() {
        if (!campaignId) return;
        setLoading(true);
        fetch(API + '?campaign_id=' + campaignId + '&limit=200', {
            headers: { Authorization: 'Bearer ' + token }
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setData(json.data || []);
            setLoading(false);
        })
        .catch(function() { setLoading(false); });
    }, [campaignId, token]);

    useEffect(function() { load(); }, [load]);
    useEffect(function() { setDatumVon(von1 || ''); setDatumBis(bis1 || ''); }, [von1, bis1]);

    function handlePreview() {
        if (!rawText || !datumVon || !datumBis) return;
        setImporting(true); setImportMsg(null); setPreview(null);
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({ preview: 1, raw: rawText, datum_von: datumVon, datum_bis: datumBis, campaign_id: campaignId, campaign_name: campaignName }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setPreview(json.rows || []);
            else setImportMsg({ ok: false, text: json.message });
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    function handleSave() {
        if (!preview) return;
        setImporting(true);
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({ save: 1, raw: rawText, datum_von: datumVon, datum_bis: datumBis, campaign_id: campaignId, campaign_name: campaignName }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) {
                setImportMsg({ ok: true, text: json.imported + ' Einträge erfasst' });
                setPreview(null); setRawText(''); setShowForm(false); load();
            } else { setImportMsg({ ok: false, text: json.message }); }
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    var periods = Array.from(new Set(data.map(function(d) { return d.datum_von + '__' + d.datum_bis; })));

    function renderItem(r, i) {
        var cls = getChangeCls(r.aenderung);
        return (
            <div key={i} className={'chg-item ' + cls}>
                <div className="chg-meta">
                    <span className="chg-time">{r.aenderung_zeit ? r.aenderung_zeit.replace('T', ' ').substring(0, 16) : '—'}</span>
                    {r.nutzer && <span className="chg-nutzer">{r.nutzer.split('@')[0]}</span>}
                </div>
                <div className="chg-text">{r.aenderung}</div>
            </div>
        );
    }

    function renderTable(rows) {
        if (rows.length === 0) return <p className="imp-empty">Keine Einträge.</p>;

        // Nach AZG gruppieren
        var KAMPAGNE_KEY = '📊 Kampagnen-Ebene';
        var groups = {};
        rows.forEach(function(r) {
            var azg = r.anzeigengruppe ? r.anzeigengruppe : KAMPAGNE_KEY;
            if (!groups[azg]) groups[azg] = [];
            groups[azg].push(r);
        });

        // Sortierung: Kampagne zuerst, dann AZGs alphabetisch
        var azgKeys = Object.keys(groups).sort(function(a, b) {
            if (a === KAMPAGNE_KEY) return -1;
            if (b === KAMPAGNE_KEY) return 1;
            return a.localeCompare(b);
        });

        // Nur eine Gruppe → direkt anzeigen
        if (azgKeys.length <= 1) {
            return <div className="chg-list">{rows.map(renderItem)}</div>;
        }

        return (
            <div>
                {azgKeys.map(function(azg) {
                    var isKampagne = azg === KAMPAGNE_KEY;
                    if (isKampagne && !showKampagne) return null;
                    if (!isKampagne && !showAZG) return null;
                    return (
                        <div key={azg} className={'chg-azg-group' + (isKampagne ? ' chg-azg-kampagne' : '')}>
                            <div className={'chg-azg-label' + (isKampagne ? ' chg-azg-label-kampagne' : '')}>
                                {azg} ({groups[azg].length})
                            </div>
                            <div className="chg-list">{groups[azg].map(renderItem)}</div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="imp-wrap">
            <div className="imp-header">
                <span className="imp-title">📋 Änderungen</span>
                <button className="imp-btn imp-btn-import" onClick={function() { setShowForm(function(s) { return !s; }); setPreview(null); setImportMsg(null); }}>
                    {showForm ? '✕ Abbrechen' : '+ Importieren'}
                </button>
            </div>

            {showForm && (
                <div className="imp-form">
                    <p className="imp-hint">Google Ads → Änderungsverlauf → Zeitraum wählen → alle Zeilen kopieren → hier einfügen.</p>
                    <div className="imp-form-row">
                        <div className="imp-field">
                            <label>Von</label>
                            <input type="date" value={datumVon} onChange={function(e) { setDatumVon(e.target.value); }} />
                        </div>
                        <div className="imp-field">
                            <label>Bis</label>
                            <input type="date" value={datumBis} onChange={function(e) { setDatumBis(e.target.value); }} />
                        </div>
                    </div>
                    <div className="imp-field">
                        <label>Daten einfügen</label>
                        <textarea rows={8} placeholder="Hier einfügen…" value={rawText} onChange={function(e) { setRawText(e.target.value); }} />
                    </div>
                    <div className="imp-form-actions">
                        <button className="imp-btn imp-btn-preview" onClick={handlePreview} disabled={importing || !rawText}>
                            {importing && !preview ? 'Prüfe…' : '🔍 Vorschau'}
                        </button>
                        {preview && (
                            <button className="imp-btn imp-btn-save" onClick={handleSave} disabled={importing}>
                                {importing ? 'Speichere…' : '💾 Erfassen (' + preview.length + ' Einträge)'}
                            </button>
                        )}
                        {importMsg && (
                            <span className={importMsg.ok ? 'imp-msg-ok' : 'imp-msg-err'}>
                                {importMsg.ok ? '✅ ' : '❌ '}{importMsg.text}
                            </span>
                        )}
                    </div>
                    {preview && preview.length > 0 && (
                        <div className="imp-preview-wrap">
                            <div className="imp-preview-title">Vorschau — {preview.length} Einträge</div>
                            {renderTable(preview)}
                        </div>
                    )}
                </div>
            )}

            {loading && <p className="imp-loading">Lädt…</p>}
            {!loading && data.length === 0 && (
                <p className="imp-empty">Noch keine Änderungen. "+ Importieren" um zu starten.</p>
            )}

            {!loading && periods.map(function(period) {
                var parts = period.split('__');
                var dVon  = parts[0];
                var dBis  = parts[1];
                var rows  = data.filter(function(d) { return d.datum_von === dVon && d.datum_bis === dBis; });
                return (
                    <div key={period} className="imp-period">
                        <div
                            className="imp-period-header imp-period-toggle"
                            onClick={function() { setCollapsedPeriods(function(p) { return {...p, [period]: p[period] === false ? true : false}; }); }}
                            style={{cursor:'pointer'}}
                        >
                            <span>{collapsedPeriods[period] !== false ? '▶' : '▼'} 📅 {dVon} – {dBis}</span>
                            <span className="imp-period-stat">{rows.length} Änderungen</span>
                            <div className="chg-filter-bar">
                                <label className="chg-filter-cb">
                                    <input type="checkbox" checked={showKampagne} onChange={function(e) { setShowKampagne(e.target.checked); }} />
                                    Kampagne
                                </label>
                                <label className="chg-filter-cb">
                                    <input type="checkbox" checked={showAZG} onChange={function(e) { setShowAZG(e.target.checked); }} />
                                    AZG
                                </label>
                            </div>
                        </div>
                        {collapsedPeriods[period] === false && renderTable(rows)}
                    </div>
                );
            })}

            {data.length > 0 && (
                <AdsDecisions bereich="aenderungen" selectedIds={selectedIds} campaignList={campaignList} />
            )}
        </div>
    );
}